import { useCallback, useMemo } from "react";

import { orderBy, uniq } from "lodash";

import { recursivelySearchForRows } from "../table/utils";
import { type TabId } from "../tabs";
import { type TopLevelBreakdown } from "../types";
import { type SalesTableRow, type SalesTableVisualRow } from "../types/table";

import { useActiveTab } from "./use-active-tab";
import { type TabState, useTabState } from "./use-tab-state";

import { useChartState, usePageConfiguration } from ".";

function filterDefaultSelectAll(
  exclusionAtoms: string[],
  inclusionAtoms: string[],
  rows: SalesTableVisualRow[],
): SalesTableVisualRow[] {
  // Parse the JSON strings into JSON objects
  const parsedExclusionAtoms: { [dimension: string]: string }[][] =
    exclusionAtoms.map((atomicity) => JSON.parse(atomicity));
  const parsedInclusionAtoms: { [dimension: string]: string }[][] =
    inclusionAtoms.map((atomicity) => JSON.parse(atomicity));
  // Filter rows where the dimensions are a subset of the jsonObjects list
  const subsetRows: SalesTableVisualRow[] = rows.filter((row) => {
    const { dimensions } = row?.rowMetadata ?? {};

    if (!parsedExclusionAtoms.length) {
      return true;
    }

    // filter out all the parents
    const exclusionParents = parsedExclusionAtoms.filter((breakdownPath) => {
      return breakdownPath.every((path) => {
        const pathKeys = Object.keys(path);
        return pathKeys.every(
          (pathKey) => dimensions[pathKey] === path[pathKey],
        );
      });
    });

    // filter out all the parents
    const inclusionParents = parsedInclusionAtoms.filter((breakdownPath) => {
      return breakdownPath.every((path) => {
        const pathKeys = Object.keys(path);
        return pathKeys.every(
          (pathKey) => dimensions[pathKey] === path[pathKey],
        );
      });
    });
    if (!exclusionParents.length) {
      return true;
    }

    const exclOrderedByDeepestParent = orderBy(
      exclusionParents,
      (a) => a.length,
      "desc",
    );
    const inclOrderedByDeepestParent = orderBy(
      inclusionParents,
      (a) => a.length,
      "desc",
    );

    return !(
      exclOrderedByDeepestParent[0].length >
      (inclOrderedByDeepestParent?.[0]?.length ?? 0)
    );
  });

  return subsetRows;
}

function filterDefaultSelectNone(
  exclusionAtoms: string[],
  inclusionAtoms: string[],
  rows: SalesTableVisualRow[],
): SalesTableVisualRow[] {
  // Parse the JSON strings into JSON objects
  const parsedExclusionAtoms: { [dimension: string]: string }[][] =
    exclusionAtoms.map((atomicity) => JSON.parse(atomicity));
  const parsedInclusionAtoms: { [dimension: string]: string }[][] =
    inclusionAtoms.map((atomicity) => JSON.parse(atomicity));
  // Filter rows where the dimensions are a subset of the jsonObjects list

  const subsetRows: SalesTableVisualRow[] = rows.filter((row) => {
    const { dimensions } = row?.rowMetadata ?? {};

    // filter out all the parents
    const exclusionParents = parsedExclusionAtoms.filter((breakdownPath) => {
      return breakdownPath.every((path) => {
        const pathKeys = Object.keys(path);
        return pathKeys.every(
          (pathKey) => dimensions[pathKey] === path[pathKey],
        );
      });
    });

    // filter out all the parents
    const inclusionParents = parsedInclusionAtoms.filter((breakdownPath) => {
      return breakdownPath.every((path) => {
        const pathKeys = Object.keys(path);
        return pathKeys.every(
          (pathKey) => dimensions[pathKey] === path[pathKey],
        );
      });
    });

    // if there are no parents, then don't select the row to be selected
    if (!exclusionParents?.length && !inclusionParents?.length) {
      return false;
    }

    const exclOrderedByDeepestParent = orderBy(
      exclusionParents,
      (a) => a.length,
      "desc",
    );
    const inclOrderedByDeepestParent = orderBy(
      inclusionParents,
      (a) => a.length,
      "desc",
    );

    return !(
      exclOrderedByDeepestParent?.[0]?.length ??
      0 > (inclOrderedByDeepestParent?.[0]?.length ?? 0)
    );
  });

  return subsetRows;
}

const SPLIT_VIEW_DEFAULT_SELECTION_LENGTH = 10;

export const shouldPreselectRows = ({
  breakdowns,
  isBreakdownTab,
  tabState,
}: {
  breakdowns: TopLevelBreakdown[];
  isBreakdownTab: boolean;
  tabState: TabState;
}) => {
  // do not preselect, if we have already selected something
  if (
    (isBreakdownTab &&
      tabState.proposedFiltersState.inclusionAtomicities?.length) ||
    (!isBreakdownTab && tabState.proposedFiltersState.inclusionAdKeys?.length)
  ) {
    return false;
  }

  if (isBreakdownTab && breakdowns.length <= 1) {
    return true;
  } else if (breakdowns.length === 0) {
    return true;
  }
  return false;
};

export function useTableRowSelection(rows: SalesTableRow[], tabId: TabId) {
  const isSplitView = useSplitView();
  const { isDefaultSelectAll } = useDefaultTableSelectionIsSelectAll();

  const { breakdowns } = usePageConfiguration();
  const { setTabState, tabState } = useTabState(tabId);

  const selectsTopRowsIfConditionsMet = useCallback(
    (rowsToSelect: SalesTableRow[]) => {
      const isBreakdownTab = tabId === "breakdown";
      if (
        isSplitView &&
        shouldPreselectRows({
          breakdowns,
          isBreakdownTab,
          tabState,
        })
      ) {
        setTabState((tabState) => ({
          ...tabState,
          preselectionDone: true,
          isPreselectionFinished: true,
          proposedFiltersState: {
            ...tabState.proposedFiltersState,
            ...(isBreakdownTab
              ? {
                  inclusionAtomicities: rowsToSelect
                    .slice(0, SPLIT_VIEW_DEFAULT_SELECTION_LENGTH)
                    .map((e) => ({ count: 1, atomicity: e.key })),
                }
              : {
                  inclusionAdKeys: rowsToSelect
                    .slice(0, SPLIT_VIEW_DEFAULT_SELECTION_LENGTH)
                    .map((row) => ({
                      adKey: row.key,
                      dimensions: (row as any).rowMetadata.dimensions ?? {},
                    })),
                }),
          },
        }));
      }

      if (
        isSplitView &&
        !shouldPreselectRows({
          breakdowns,
          isBreakdownTab,
          tabState,
        })
      ) {
        setTabState((tabState) => ({
          ...tabState,
          isPreselectionFinished: true,
        }));
      }
    },

    [isSplitView, breakdowns, tabId, tabState, setTabState],
  );

  const selectedRowIds = useMemo(() => {
    // if in exclusionary mode, do your exclusions
    if (isSplitView) {
      return uniq([
        ...tabState.proposedFiltersState.inclusionAdKeys.map((i) => i.adKey),
        ...tabState.proposedFiltersState.inclusionAtomicities.map(
          (i) => i.atomicity,
        ),
      ]);
    } else {
      const initialSetOfRows = recursivelySearchForRows(rows);

      const dimensionFiltered = isDefaultSelectAll
        ? filterDefaultSelectAll(
            tabState.proposedFiltersState.exclusionAtomicities.map(
              (c) => c.atomicity,
            ),
            tabState.proposedFiltersState.inclusionAtomicities.map(
              (c) => c.atomicity,
            ),
            initialSetOfRows,
          )
        : filterDefaultSelectNone(
            tabState.proposedFiltersState.exclusionAtomicities.map(
              (c) => c.atomicity,
            ),
            tabState.proposedFiltersState.inclusionAtomicities.map(
              (c) => c.atomicity,
            ),
            initialSetOfRows,
          );

      const exclusionAdKeysSet = new Set(
        tabState.proposedFiltersState.exclusionAdKeys.map((a) => a.adKey),
      );
      const adKeyFiltered = dimensionFiltered.filter(
        (r) => !exclusionAdKeysSet.has(r.key),
      );

      const allExclusions = adKeyFiltered.map((r) => r.key);

      return uniq([
        ...allExclusions,
        ...tabState.proposedFiltersState.inclusionAdKeys.map((i) => i.adKey),
        ...tabState.proposedFiltersState.inclusionAtomicities.map(
          (i) => i.atomicity,
        ),
      ]);
    }
  }, [
    isDefaultSelectAll,
    isSplitView,
    rows,
    tabState.proposedFiltersState.exclusionAdKeys,
    tabState.proposedFiltersState.exclusionAtomicities,
    tabState.proposedFiltersState.inclusionAdKeys,
    tabState.proposedFiltersState.inclusionAtomicities,
  ]);

  return { selectedRowIds, selectsTopRowsIfConditionsMet };
}

export function useSplitView() {
  const { chartConfigs } = useChartState();

  const isSplitMode = useMemo(
    () => chartConfigs.some((c) => c.chartView === "split"),
    [chartConfigs],
  );

  return isSplitMode;
}

export function useDefaultTableSelectionIsSelectAll() {
  const { activeTab } = useActiveTab();
  const { tabState } = useTabState(activeTab);

  return { isDefaultSelectAll: tabState.selectAll };
}
